import { Box, CircularProgress, Typography } from "@mui/material";
import useGlobalContext from "hooks/globalData";
import React, { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  Certificate,
  CertificateTemplate,
  CertificateTemplatesOverrideExtended,
  CertificateTheme,
  ListResponseOfCertificateTemplatesOverrideExtended,
  ResponseOfCertificateTemplate,
  ResponseOfCertificateTheme,
} from "services/KnowfullyAdminClient";
import { Orientation } from "types/common-types";
import CertificatePreviewBox, {
  CertificatePreviewBoxProps,
  CertificateTemplateType,
} from "./CertificatePreviewBox";
import { orderBy } from "lodash";

type CertificatePreviewProps = {
  certificate: Certificate;
};

const bodyTag = "[[BODY]]";

const CertificatePreview = ({ certificate }: CertificatePreviewProps) => {
  const { certificateService } = useGlobalContext();
  const [rows, setRows] = useState<CertificatePreviewBoxProps[]>([]);
  const { formatMessage: t } = useIntl();

  const {
    data: certificateTemplateData,
    isLoading: isLoadingCertificateTemplate,
  } = useQuery<ResponseOfCertificateTemplate>(
    ["certificaTemplatePreview", certificate?.certificateTemplateId],
    () =>
      certificateService.getCertificateTemplate(
        certificate?.certificateTemplateId!
      ),
    {
      enabled: !!certificate && !!certificate?.certificateTemplateId!,
      cacheTime: 0,
    }
  );

  const {
    data: certificateThemeData,
    isLoading: isLoadingCertificateTheme,
  } = useQuery<ResponseOfCertificateTheme>(
    ["certificaThemePreview", certificate?.certificateThemeId],
    () =>
      certificateService.getCertificateTheme(certificate?.certificateThemeId!),
    {
      enabled: !!certificate && !!certificate?.certificateThemeId!,
      cacheTime: 0,
    }
  );

  const {
    data: certificateTemplatesOverrideData,
    isLoading: isLoadingCertificateTemplatesOverrides,
  } = useQuery<ListResponseOfCertificateTemplatesOverrideExtended>(
    [
      "certificateTemplatesOverridesPreview",
      certificate?.certificateTemplateId,
    ],
    () =>
      certificateService.getCertificateTemplatesOverrides(
        certificate?.certificateTemplateId!,
        null
      ),
    {
      enabled: !!certificate && !!certificate?.certificateTemplateId!,
      cacheTime: 0,
    }
  );

  const certificateTemplateOverrides: CertificateTemplatesOverrideExtended[] = certificateTemplatesOverrideData?.data! as CertificateTemplatesOverrideExtended[];
  const certificateTemplate: CertificateTemplate = certificateTemplateData?.data! as CertificateTemplate;
  const certificateTheme: CertificateTheme = certificateThemeData?.data! as CertificateTheme;

  useEffect(() => {
    if (
      !isLoadingCertificateTheme &&
      !isLoadingCertificateTemplate &&
      !isLoadingCertificateTemplatesOverrides &&
      rows.length === 0
    ) {
      const defaultTemplate = {
        name: "Default",
        type: CertificateTemplateType.Default,
        orientation: certificateTemplate.isLandscape
          ? Orientation.Landscape
          : Orientation.Portrait,
        html:
          certificateTheme?.themeHtml != null
            ? certificateTheme?.themeHtml?.replace(
                bodyTag,
                certificateTemplate?.templateHtml!
              )
            : certificateTemplate?.templateHtml!,
      } as CertificatePreviewBoxProps;
      setRows((prev) => [...prev, defaultTemplate]);

      const certificateOverrides = certificateTemplateOverrides.map(
        (override) => {
          return {
            name: override.isCertificateOfCompletion
              ? t({ id: "certificateCompletionLabel" })
              : t(
                  {
                    id: "overrideNameLabel",
                  },
                  {
                    profession: override.profession,
                  }
                ),
            type: override.isCertificateOfCompletion
              ? CertificateTemplateType.Completion
              : CertificateTemplateType.Override,
            orientation: override.isLandscape
              ? Orientation.Landscape
              : Orientation.Portrait,
            html:
              certificateTheme?.themeHtml != null
                ? certificateTheme?.themeHtml?.replace(
                    bodyTag,
                    override?.templateHtmlOverride!
                  )
                : override?.templateHtmlOverride!,
          } as CertificatePreviewBoxProps;
        }
      );
      setRows((prev) => [
        ...prev,
        ...orderBy(certificateOverrides, ["type"], ["asc"]),
      ]);
    }
  }, [
    certificate,
    certificateTemplate,
    certificateTemplateOverrides,
    certificateTheme?.themeHtml,
    isLoadingCertificateTemplate,
    isLoadingCertificateTemplatesOverrides,
    isLoadingCertificateTheme,
    rows.length,
    t,
  ]);

  return (
    <>
      <Box my={3}>
        <Typography variant="h5">
          <T id="previewTemplatesAndOverridesLabel" />
        </Typography>
        <Box my={2}>
          {!isLoadingCertificateTheme &&
          !isLoadingCertificateTemplatesOverrides &&
          !isLoadingCertificateTemplate ? (
            rows.map((certificateTemplate) => (
              <div key={certificateTemplate.name}>
                <CertificatePreviewBox {...certificateTemplate} />
              </div>
            ))
          ) : (
            <CircularProgress size={32} />
          )}
        </Box>
      </Box>
    </>
  );
};

export default CertificatePreview;
